/**
 * 应用安装检测工具
 * 用于检测手机上某个应用是否已安装
 */

/**
 * 方法1: 使用 URL Scheme 检测（推荐，兼容性好，适配 WebView 环境）
 * @param {Object} options 配置选项
 * @param {String} options.scheme - 应用的 URL Scheme（如：'preeducationsz://'）
 * @param {String} options.packageName - Android 包名（如：'com.iflytek.preeducation'）
 * @param {String} options.intentUrl - Android Intent URL（可选）
 * @param {Number} options.timeout - 超时时间（毫秒），默认根据设备类型自动设置
 * @returns {Promise<Boolean>} 返回 Promise，resolve(true) 表示已安装，resolve(false) 表示未安装
 */
let isChecking = false // 防抖标记

export function checkAppInstalled (options = {}) {
  const {
    scheme = 'preeducationsz://',
    packageName = 'com.iflytek.preeducation',
    intentUrl = null,
    timeout = null // 自动根据设备类型设置
  } = options

  return new Promise((resolve) => {
    // 防抖：如果正在检测，直接返回 false
    if (isChecking) {
      resolve(false)
      return
    }
    isChecking = true

    const ua = navigator.userAgent.toLowerCase()
    const isIOS = /iPhone|iPad|iPod/i.test(ua)
    const isAndroid = /Android/i.test(ua)
    // 检测是否是 iOS Safari（不是 WebView）
    // const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua)
    // const isIOSSafari = isIOS && isSafari

    // 如果不是移动端，直接返回 false
    if (!isIOS && !isAndroid) {
      isChecking = false
      resolve(false)
      return
    }

    const startTime = Date.now()
    let timer = null
    let isResolved = false
    // iOS 使用较短的超时时间，因为如果应用已安装，页面会很快隐藏
    // Safari 中如果应用未安装会显示错误提示，但我们可以快速检测以减少显示时间
    // WebView 中较短的超时时间：iOS 400ms，Android 600ms
    const checkTimeout = timeout || (isIOS ? 400 : 600)

    // 清理函数
    const cleanup = () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler)
      document.removeEventListener('webkitvisibilitychange', visibilityChangeHandler)
      if (timer) {
        clearTimeout(timer)
      }
      isChecking = false
    }

    // 监听页面可见性变化（WebView 中最可靠的方法）
    const visibilityChangeHandler = () => {
      if (isResolved) return
      const elapsed = Date.now() - startTime
      // 如果页面隐藏，说明应用打开了
      if (document.hidden || document.webkitHidden) {
        if (elapsed < checkTimeout + 500) {
          isResolved = true
          clearTimeout(timer)
          cleanup()
          resolve(true)
        }
      }
    }

    // 设置超时
    timer = setTimeout(() => {
      if (!isResolved) {
        isResolved = true
        cleanup()
        // 超时后再次检查页面状态（Safari iframe 方式可能需要额外时间）
        if (document.hidden || document.webkitHidden) {
          // 页面已隐藏，说明应用打开了
          resolve(true)
        } else {
          // 页面未隐藏，说明应用未安装
          resolve(false)
        }
      }
    }, checkTimeout)

    // 绑定事件监听（支持 webkit 前缀）
    document.addEventListener('visibilitychange', visibilityChangeHandler)
    document.addEventListener('webkitvisibilitychange', visibilityChangeHandler)

    // 尝试打开应用
    try {
      let invokeUrl = ''

      if (isIOS) {
        invokeUrl = scheme
      } else if (isAndroid) {
        // Android 使用 Intent，增加 fallback 避免阻塞
        invokeUrl = intentUrl || `intent://#Intent;scheme=${scheme.replace('://', '')};package=${packageName};S.browser_fallback_url=${encodeURIComponent('about:blank')};end`
      }

      // iOS Safari 和 WebView 都使用 a 标签方式
      // 注意：在 Safari 中如果应用未安装，可能会显示错误提示，这是 Safari 的限制
      // 但我们可以通过快速检测（短超时）来减少错误提示的显示时间
      if (isIOS) {
        // iOS：使用 a 标签点击（Safari 和 WebView 都支持）
        const link = document.createElement('a')
        link.href = invokeUrl
        link.style.display = 'none'
        document.body.appendChild(link)

        try {
          // 触发原生点击事件
          link.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          }))
        } catch (e) {
          // 降级方案：直接调用 click
          link.click()
        }

        // 延迟移除，确保事件触发
        setTimeout(() => {
          try {
            if (link.parentNode) {
              document.body.removeChild(link)
            }
          } catch (e) {
            // 忽略清理错误
          }
        }, 100)
      } else if (isIOS) {
        // iOS WebView：使用 a 标签（可以正常打开应用）
        const link = document.createElement('a')
        link.href = invokeUrl
        link.style.display = 'none'
        document.body.appendChild(link)

        try {
          // 触发原生点击事件
          link.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          }))
        } catch (e) {
          // 降级方案：直接调用 click
          link.click()
        }

        // 延迟移除，确保事件触发
        setTimeout(() => {
          if (link.parentNode) {
            document.body.removeChild(link)
          }
        }, 100)
      } else if (isAndroid) {
        // Android 使用 a 标签点击（WebView 中更可靠）
        const link = document.createElement('a')
        link.href = invokeUrl
        link.style.display = 'none'
        document.body.appendChild(link)

        try {
          // 触发原生点击事件
          link.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          }))
        } catch (e) {
          // 降级方案：直接调用 click
          link.click()
        }

        // 延迟移除，确保事件触发
        setTimeout(() => {
          if (link.parentNode) {
            document.body.removeChild(link)
          }
        }, 100)
      }
    } catch (error) {
      console.error('检测应用安装时出错:', error)
      cleanup()
      resolve(false)
    }
  })
}

/**
 * 方法2: 使用 callapp-lib 库检测（项目中已安装）
 * @param {Object} options 配置选项
 * @param {String} options.scheme - 应用的 URL Scheme
 * @param {String} options.packageName - Android 包名
 * @param {String} options.appStore - App Store 链接
 * @param {String} options.fallback - 应用商店链接（Android）
 * @returns {Promise<Boolean>} 返回 Promise，resolve(true) 表示已安装，resolve(false) 表示未安装
 */
export function checkAppInstalledByCallApp (options = {}) {
  return new Promise((resolve) => {
    try {
      // 动态导入 callapp-lib（如果未安装会报错）
      import('callapp-lib').then(({ default: CallApp }) => {
        const {
          scheme = 'qiuqiu://',
          packageName = 'com.iflytek.preeducation',
          appStore = 'https://apps.apple.com/cn/app/id1507879856',
          fallback = 'https://a.app.qq.com/o/simple.jsp?pkgname=com.iflytek.preeducation'
        } = options

        // callapp-lib 没有直接的检测方法，需要通过尝试打开来判断
        // 这里结合方法1来实现
        // eslint-disable-next-line no-unused-vars
        const callApp = new CallApp({
          scheme: {
            protocol: scheme,
            path: ''
          },
          appstore: appStore,
          fallback: fallback,
          timeout: 2000
        })

        checkAppInstalled({ scheme, packageName }).then(resolve)
      }).catch(() => {
        // 如果 callapp-lib 未安装，使用基础方法
        checkAppInstalled(options).then(resolve)
      })
    } catch (error) {
      console.error('使用 callapp-lib 检测失败:', error)
      checkAppInstalled(options).then(resolve)
    }
  })
}

/**
 * 方法3: 简单的同步检测（不推荐，准确性较低）
 * 通过尝试打开应用，根据页面是否失去焦点来判断
 * @param {String} scheme - 应用的 URL Scheme
 * @param {Function} callback - 回调函数，参数为 (error, isInstalled)
 */
export function checkAppInstalledSync (scheme = 'qiuqiu://', callback) {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  const isAndroid = /Android/i.test(navigator.userAgent)

  if (!isIOS && !isAndroid) {
    callback(null, false)
    return
  }

  const startTime = Date.now()
  let isInstalled = false

  const blurHandler = () => {
    const elapsed = Date.now() - startTime
    if (elapsed < 1000) {
      isInstalled = true
      window.removeEventListener('blur', blurHandler)
      callback(null, true)
    }
  }

  window.addEventListener('blur', blurHandler)

  // 尝试打开应用
  try {
    window.location.href = scheme
  } catch (error) {
    console.error('检测应用安装时出错:', error)
    callback(error, false)
    return
  }

  // 延迟检查，如果页面没有失去焦点，说明应用未安装
  setTimeout(() => {
    if (!isInstalled) {
      window.removeEventListener('blur', blurHandler)
      callback(null, false)
    }
  }, 2000)
}

/**
 * 方法3扩展: 支持自定义超时时间的同步检测，使用 visibilitychange 检测页面是否进入后台
 * @param {String} scheme - 应用的 URL Scheme
 * @param {Function} callback - 回调函数，参数为 (error, isInstalled)
 * @param {Number} timeout - 超时时间（毫秒），默认 2000
 */
export function checkAppInstalledSyncWithTimeout (scheme = 'qiuqiu://', callback, timeout = 2000) {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  const isAndroid = /Android/i.test(navigator.userAgent)

  if (!isIOS && !isAndroid) {
    callback(null, false)
    return
  }

  let isResolved = false
  let timer = null

  const cleanup = () => {
    document.removeEventListener('visibilitychange', visibilityChangeHandler)
    document.removeEventListener('webkitvisibilitychange', visibilityChangeHandler)
    if (timer) clearTimeout(timer)
  }

  const visibilityChangeHandler = () => {
    if (isResolved) return
    if (document.hidden || document.webkitHidden) {
      isResolved = true
      cleanup()
      callback(null, true)
    }
  }

  document.addEventListener('visibilitychange', visibilityChangeHandler)
  document.addEventListener('webkitvisibilitychange', visibilityChangeHandler)

  // 尝试打开应用
  try {
    window.location.href = scheme
  } catch (error) {
    console.error('检测应用安装时出错:', error)
    cleanup()
    callback(error, false)
    return
  }

  // 超时后页面仍可见，说明应用未安装
  timer = setTimeout(() => {
    if (!isResolved) {
      isResolved = true
      cleanup()
      callback(null, false)
    }
  }, timeout)
}

/**
 * 检测熊小球应用是否已安装
 * @returns {Promise<Boolean>}
 */
export function checkQiuQiuAppInstalled () {
  return checkAppInstalled({
    scheme: 'qiuqiu://',
    packageName: 'com.iflytek.preeducation',
    intentUrl: 'intent://#Intent;scheme=qiuqiu;package=com.iflytek.preeducation;end',
    timeout: 2000
  })
}

/**
 * 检测拼音应用是否已安装
 * @returns {Promise<Boolean>}
 */
export function checkPinyinAppInstalled () {
  return checkAppInstalled({
    scheme: 'qiuqiupy://',
    packageName: 'com.iflytek.preeducationpy',
    intentUrl: 'intent://#Intent;scheme=qiuqiupy;package=com.iflytek.preeducationpy;end',
    timeout: 2000
  })
}

export default {
  checkAppInstalled,
  checkAppInstalledByCallApp,
  checkAppInstalledSync,
  checkAppInstalledSyncWithTimeout,
  checkQiuQiuAppInstalled,
  checkPinyinAppInstalled
}
